import routes from '.';

import {
  useGetFeatureFlagsQuery,
  useGetPaidPlanDetailsQuery,
} from 'src/services/dashboard-api';
import { useGetOrgConfigQuery } from 'src/services/settings/settingsApi';
import { Routes, UserRoles } from 'src/utils/enums';

export const usePermittedRoutes = ({
  // @TODO, permission probably should live in Redux instead of top level app internal state
  permissionKeys,
  role,
}: {
  permissionKeys: string[];
  role: UserRoles;
}) => {
  const { data, isLoading } = useGetFeatureFlagsQuery();
  const { data: orgConfigData } = useGetOrgConfigQuery();
  const { data: paidPlanDetails } = useGetPaidPlanDetailsQuery();
  const permissionSet = new Set(permissionKeys);

  if (isLoading) {
    return [];
  }

  return routes
    .map(route => {
      // SSO settings configure the dashboard's own Cognito/SAML auth, which
      // is bypassed in the Zendesk build (auth rides Zendesk's session
      // cookies — see initApp.tsx and zendeskAuth.ts). Drop the route so
      // direct navigation to /sso-settings also 404s.
      if (route.path === Routes.SSO_SETTINGS && IS_ZENDESK_BUILD) {
        return null;
      }

      const { featureFlags: routeFeatureFlags, permissionKey, roles } = route;
      const permissionsRequired = permissionKey ?? roles;
      const doesRouteHaveRequiredFeatureFlags = routeFeatureFlags?.length
        ? routeFeatureFlags.every(flag => {
            // Special handling for is_assist_gpt_enabled - check org config instead of feature flags
            if (flag === 'is_assist_gpt_enabled') {
              return orgConfigData?.is_assist_gpt_enabled === true;
            }
            return data?.feature_flags?.includes(flag);
          })
        : true;

      if (route.ignorePermissionForSuperAdmin && role === 'ROLE_SUPER_ADMIN') {
        return route;
      }

      if (
        permissionsRequired &&
        permissionKey &&
        !permissionSet.has(permissionKey)
      ) {
        return null;
      }

      if (permissionsRequired && roles && !roles.has(role)) {
        return null;
      }

      if (!doesRouteHaveRequiredFeatureFlags) {
        return null;
      }

      if (route.requiredTiers?.length) {
        if (
          !paidPlanDetails?.tier ||
          !route.requiredTiers.includes(paidPlanDetails.tier)
        ) {
          return null;
        }
      }

      // Sidebar is hidden for Agent role (if they have access to routes)
      if (role === UserRoles.ROLE_AGENT) {
        return {
          ...route,
          showSideBar: false,
        };
      }

      // Hide navbar for ROUTE_WORKFLOW_BUILDER when workflow_builder_style_revamp feature flag is enabled
      if (
        (route.path === Routes.WORKFLOW_BUILDER ||
          route.path === Routes.ASSIST_WORKFLOW_BUILDER) &&
        data?.feature_flags?.includes('workflow_builder_style_revamp')
      ) {
        return {
          ...route,
          showNavBar: false,
        };
      }

      // Hide navbar for DISCOVER_INSIGHTS (page renders its own NavbarV2 with tabs)
      if (route.path === Routes.DISCOVER_INSIGHTS) {
        return {
          ...route,
          showNavBar: false,
        };
      }

      // Hide navbar for DISCOVER_TOPIC_DETAIL (page renders its own NavbarV2 with back link)
      if (route.path === Routes.DISCOVER_TOPIC_DETAIL) {
        return {
          ...route,
          showNavBar: false,
        };
      }

      return route;
    })
    .filter(Boolean);
};
